/**
 * The contract between the code and the "Ka floating" Rive file
 * (`ka-floating.riv`): Ka in his rounded card, following the cursor, writing
 * behind his paper and peeking out of the frame now and then. Every name is
 * read from here, never typed twice.
 *
 *   Artboard       `Ka` (128×128, transparent). The card (cream fill, amber
 *                  border) is 64×64 at x 32–96, y 35–99; the margin around it
 *                  is where Ka's head goes when he peeks — never clip it.
 *   State machine  `Ka` (also the file's default)
 *   View model     `Ka`, default instance
 *     lookX, lookY (number)  the cursor, in artboard px from Ka's eyes
 *                            (0, 0 = ahead). Rive's own listeners set them
 *                            over the canvas; a page writes them from window
 *                            pointer events so Ka follows it anywhere.
 *     write (trigger)        ~3 s: Ka looks down and scribbles behind his
 *                            paper, then idles
 *     peek (trigger)         ~3.2 s: leans out past the left OR right edge,
 *                            looks around, comes back
 *   Built in: the idle loop (breathing, sway, blinks), and at the end of each
 *   5 s cycle a random pick — idle 60%, write 20%, peek 20%. A trigger during
 *   a write or peek is ignored. How far and how often lives in the file.
 */
import { riveLook } from './look';

export const KA_FLOATING_RIVE = {
  src: '/rive/ka-floating.riv',
  artboard: 'Ka',
  stateMachine: 'Ka',
  viewModel: 'Ka',
  /** One idle cycle — the file picks idle / write / peek at the end of each. */
  cycleMs: 5000,
  /** Intrinsic artboard size — keep the canvas square. */
  width: 128,
  height: 128,
  /** Midpoint of Ka's eyes in artboard px — the origin of lookX / lookY. */
  eyes: { x: 63.567, y: 62.035 },
  /** The card inside the artboard: it fills the canvas's middle half. */
  card: { x: 32, y: 35, size: 64 },
  props: {
    lookX: 'lookX',
    lookY: 'lookY',
    write: 'write',
    peek: 'peek',
  },
  /** `onStateChange` names. */
  states: {
    idle: 'idle',
    write: 'write',
    peekRight: 'peek_right',
    peekLeft: 'peek_left',
  },
} as const;

/** lookX / lookY for a pointer at client coordinates over this file's canvas
 *  (`Fit.Contain`, centred). */
export const kaFloatingLook = (
  clientX: number,
  clientY: number,
  canvas: { left: number; top: number; width: number; height: number },
) => riveLook(clientX, clientY, canvas, KA_FLOATING_RIVE);
